import { FormCheckbox } from "@components/FormCheckbox";
import { FormInput } from "@components/FormInput";
import { FormSwitch } from "@components/FormSwitch";
import Spacer from "@components/Spacer";
import { Container } from "@components/crud/Container";
import { Form } from "@components/crud/Form";
import { Loader } from "@components/crud/Loader";
import { Toolbar } from "@components/crud/Toolbar";
import { FormLabel, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { styled } from "@mui/material/styles";
import {
  ModelPermission,
  PermissionAction,
  useAdminPermissionGet,
  useAdminRoleRoleIdGet
} from "@sportsgravyengineering/sg-api-react-sdk";
import { useFormDefaultValues } from "@utils/useFormDefaultValues";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { organizationAtom } from "../../recoil/auth";
import { useRecoilState } from "recoil";
import { hasPermission } from "@services/Casbin";
import { capitalize } from "@utils/capitalize";

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: "0.25rem",

  "& .MuiFormLabel-asterisk": {
    color: theme.palette.error.main
  }
}));
const StyledDiv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  backgroundColor: "#F4F6F9",
  padding: "8px 20px 8px 0",
  minWidth: 600,
  width: "65%",
  [theme.breakpoints.down("xl")]: {
    width: "100%"
  }
}));
export const RoleView = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [organizationId] = useRecoilState(organizationAtom);
  const [actionPermissions, setActionPermissions] = useState({
    edit: false
  });
  const renderChildren = (
    children: ModelPermission[],
    control,
    setValue,
    indentLevel = 1
  ) => {
    return (
      <>
        {children
          .filter(
            (c) =>
              role?.data.permissions?.some(
                (rp) => rp.permissionId === c.permissionId
              )
          )
          .map((child) => {
            const key = child.permissionId?.replace(/\./g, "__");
            const override = role?.data.permissions
              ?.find((rp) => rp.permissionId === child.permissionId)
              ?.permission?.roleTypeAliasPermissions?.find(
                (rtap) =>
                  rtap.roleAlias === role?.data.alias &&
                  rtap.roleType === role.data.type
              );
            if (override) {
              child.name = override.nameOverride;
              child.actions = override.allowActions;
            }
            return (
              <div data-testid={child.permissionId} key={key}>
                <StyledDiv>
                  <Typography
                    variant="permissionNames"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      width: 300,
                      paddingLeft: `${indentLevel * 20}px`
                    }}
                  >
                    {child.name}
                  </Typography>

                  <div
                    style={{ display: "flex", flexDirection: "row", flex: 1 }}
                  >
                    {child.type === "MULTI" &&
                      ["ALL", "VIEW", "ADD", "EDIT", "DELETE"].map((action) => {
                        if (
                          child.actions?.includes(action as PermissionAction)
                        ) {
                          return (
                            <FormCheckbox
                              key={`${key}__${action}`}
                              control={control}
                              name={`${key}__${action}`}
                              label={action[0] + action.slice(1).toLowerCase()}
                              labelPadding="4px 0"
                              labelTypographyProps={{
                                variant: "body1",
                                marginLeft: "8px"
                              }}
                              disabled={true}
                            />
                          );
                        } else {
                          return (
                            <div
                              key={`${key}__${action}`}
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                width: "100%"
                              }}
                            />
                          );
                        }
                      })}
                    {child.type === "BOOL" && (
                      <FormSwitch
                        control={control}
                        name={`${key}`}
                        disabled={true}
                      />
                    )}
                  </div>
                </StyledDiv>

                {child.children &&
                  renderChildren(
                    child.children,
                    control,
                    setValue,
                    indentLevel + 1
                  )}
              </div>
            );
          })}
        {indentLevel < 3 && (
          <Spacer pb={8} style={{ backgroundColor: "#FFF", maxWidth: 800 }} />
        )}
      </>
    );
  };

  const {
    data: role,
    isFetching: isRoleFetching,
    error: error
  } = useAdminRoleRoleIdGet(roleId as string);
  useEffect(() => {
    if (error?.code == "ERR_BAD_REQUEST") navigate("/not-found");
  }, [error]);
  const { data: permissions, isFetching: isPermissionsFetching } =
    useAdminPermissionGet({
      organizationId
    });

  const { control, setValue, reset } = useForm({
    mode: "onBlur"
  });

  useFormDefaultValues(
    reset,
    () => {
      const rolePermissions =
        role?.data?.permissions?.reduce((acc, permission) => {
          const key = permission.permissionId?.replace(/\./g, "__") || "";
          switch (permission.permission?.type) {
            case "BOOL":
              acc[key] = permission.actions?.includes("ON");
              break;
            case "MULTI":
              permission.actions?.map((action) => {
                acc[key + "__" + action] = true;
              });
              break;
          }
          return acc;
        }, {}) || {};

      return {
        name: role?.data?.name || "",
        displayText: role?.data?.displayText || "",
        description: role?.data?.description || "",
        type: capitalize(role?.data?.type as string) || "",
        ...rolePermissions
      };
    },
    [role, permissions]
  );

  useEffect(() => {
    const checkPermission = async (permissionId, permission) => {
      const res = await hasPermission(
        "ORGANIZATION",
        organizationId!,
        permissionId as string,
        permission as string
      );
      return res;
    };
    const fetchPermissions = async () => {
      const edit = await checkPermission("user.roles", "EDIT");
      setActionPermissions({
        edit
      });
    };
    fetchPermissions();
  }, [organizationId]);

  return (
    <Container>
      <Toolbar
        title="View Role &amp; Permissions"
        backBtnClick={() => navigate("/roles")}
        editBtnClick={
          role?.data.alias === "ADMIN" ||
          !actionPermissions.edit ||
          isRoleFetching ||
          isPermissionsFetching
            ? undefined
            : () => navigate(`/roles/${roleId}/edit`)
        }
      />
      <Form>
        <Loader isLoading={isRoleFetching || isPermissionsFetching}>
          <Grid container spacing={2}>
            <Grid xs={12} md={6} data-testid="role-name">
              <FormInput
                control={control}
                name="name"
                type="text"
                label="Name"
                required={true}
                disabled={true}
                InputProps={{
                  placeholder: role?.data?.name
                }}
              />
            </Grid>
            {!organizationId && (
              <Grid xs={12} md={6} data-testid="role-type">
                <FormInput
                  control={control}
                  name="type"
                  type="text"
                  label="Role Type"
                  InputProps={{
                    placeholder: role?.data?.type
                  }}
                  required={true}
                  disabled={true}
                />
              </Grid>
            )}
            {!organizationId && (
              <Grid xs={12} md={6} data-testid="role-displayText">
                <FormInput
                  control={control}
                  name="displayText"
                  type="text"
                  label="Display Text"
                  required={true}
                  disabled={true}
                  InputProps={{
                    placeholder: role?.data?.displayText
                  }}
                />
              </Grid>
            )}
            <Grid xs={12} data-testid="role-description">
              <FormInput
                control={control}
                name="description"
                type="text"
                label="Description"
                required={true}
                disabled={true}
                InputProps={{
                  placeholder: role?.data?.description
                }}
                multiline={true}
                TextProps={{
                  rows: 2
                }}
              />
            </Grid>
            <Grid data-testid="permissions" xs={12}>
              <StyledFormLabel required={true}>
                <Typography display="inline" variant="formLabel">
                  Permissions
                </Typography>
              </StyledFormLabel>
              {permissions?.data
                ?.filter(
                  (permission) =>
                    role?.data.permissions?.some(
                      (rp) =>
                        rp.permission?.parentId === permission.permissionId
                    )
                )
                .map((permission) => {
                  const key = permission.permissionId?.replace(/\./g, "__");
                  return (
                    <React.Fragment key={key}>
                      <Spacer mt={8} />
                      <Typography
                        data-testid={permission.permissionId}
                        sx={{
                          textTransform: "uppercase",
                          color: "#ccc",
                          fontWeight: "bold"
                        }}
                      >
                        {permission.name}
                      </Typography>
                      {renderChildren(permission.children!, control, setValue)}
                    </React.Fragment>
                  );
                })}
            </Grid>
          </Grid>
        </Loader>
      </Form>
    </Container>
  );
};
